import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import {
  type AddressSnapshotFields,
  type BlockingVerificationStatus,
  isAddressValidForVerification,
} from "../addressVerification";
import type { AddressVerificationModalState } from "../useAddressVerificationGate";

const MESSAGE_KEYS = {
  WarningChanged: "review.customer.residence.addressVerification.messages.warningChanged",
  ErrorSubpremise: "review.customer.residence.addressVerification.messages.errorSubpremise",
  ErrorFailed: "review.customer.residence.addressVerification.messages.errorFailed",
} as const satisfies Record<BlockingVerificationStatus, string>;

const IGNORE_CONFIRM_KEYS = {
  ErrorSubpremise: "review.customer.residence.addressVerification.ignoreConfirm.errorSubpremise",
  ErrorFailed: "review.customer.residence.addressVerification.ignoreConfirm.errorFailed",
} as const satisfies Record<Exclude<BlockingVerificationStatus, "WarningChanged">, string>;

// ---------------------------------------------------------------------------
// AddressVerificationModal (address-standardization-spec.md §8)
//
// Single modal collapsing legacy's two nested popups (suggested-vs-actual
// picker + ignore confirmation). Built on the same Dialog/DialogContent/
// DialogFooter primitives as CallScriptModal.tsx's confirm-save dialog.
//
// Both "You Entered" and "USPS Suggested" are read-only text — there's no
// in-modal editing. To fix an address, the analyst uses "Edit address" (spec
// §8.1's ErrorSubpremise/ErrorFailed path), which closes this modal and lets
// them fix the fields on the main form.
// ---------------------------------------------------------------------------

interface AddressVerificationModalProperties {
  state?: AddressVerificationModalState;
  onSelect: (fields: AddressSnapshotFields) => void;
  onIgnore: () => void;
  /** Called with the "You Entered" address, so the analyst can fix it on the main form. */
  onEdit: (fields: AddressSnapshotFields) => void;
  onCancel: () => void;
}

interface AddressDisplayPanelProperties {
  title: string;
  fields: AddressSnapshotFields;
  invalid?: boolean;
}

/** Read-only rendering of an address as plain text. */
function AddressDisplayPanel({ title, fields, invalid }: AddressDisplayPanelProperties) {
  const { t } = useTranslation();
  const apt = fields.apartmentNumber.trim();
  const street = [fields.streetNumber, fields.streetName].filter((part) => part.trim() !== "").join(" ");
  const streetLine = [street, apt && `${t("review.customer.residence.addressVerification.aptDisplayPrefix")} ${apt}`]
    .filter(Boolean)
    .join(", ");
  const cityLine = [fields.city.trim(), [fields.state.trim(), fields.zip.trim()].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  const lines = [streetLine, cityLine].filter((line) => line !== "");

  return (
    <div className="flex flex-1 flex-col items-center gap-2">
      <h4 className="text-primary text-xs font-semibold">{title}</h4>
      <div className="text-center text-sm">
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      {invalid && (
        <p role="alert" className="text-destructive text-2xs">
          {t("review.customer.residence.addressVerification.invalidPanel")}
        </p>
      )}
    </div>
  );
}

export function AddressVerificationModal({
  state,
  onSelect,
  onIgnore,
  onEdit,
  onCancel,
}: AddressVerificationModalProperties) {
  const { t } = useTranslation();
  const [confirmingIgnore, setConfirmingIgnore] = useState(false);

  // Reset local UI state whenever a new slot's modal opens.
  useEffect(() => {
    setConfirmingIgnore(false);
  }, [state]);

  if (!state) {
    return (
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent />
      </Dialog>
    );
  }

  const personLabel = t(state.slot.personPath === "driver" ? "signers.driver" : "signers.secondSigner");
  const slotLabel = t(
    state.slot.kind === "physical"
      ? "review.customer.residence.physicalAddress"
      : "review.customer.residence.mailingAddress"
  );
  const message = t(MESSAGE_KEYS[state.status]);
  const entered = state.entered;
  const suggested = state.suggested;
  // USPS's own suggestion isn't guaranteed to satisfy the same pre-flight
  // check `entered` already passed — a malformed response (e.g. bad state/zip)
  // shouldn't be selectable, so "Select Suggested" is disabled for it and the
  // analyst falls back to "Select Entered" or Cancel.
  const suggestedInvalid = suggested !== undefined && !isAddressValidForVerification(suggested);

  function handleSelectEntered() {
    // Unlike `suggested`, `entered` already passed isAddressValidForVerification
    // before this modal could open (useAddressVerificationGate.processSlot) —
    // no re-check needed.
    onSelect(entered);
  }

  function handleSelectSuggested() {
    if (!suggested || suggestedInvalid) {
      return;
    }
    onSelect(suggested);
  }

  function handleEditAddress() {
    onEdit(entered);
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent showCloseButton={false} className="gap-0 overflow-hidden p-0 sm:max-w-lg">
        <div className="bg-primary text-primary-foreground flex items-center justify-between px-5 py-2.5">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">
            {t("review.customer.residence.addressVerification.title", { personLabel, slotLabel })}
          </DialogTitle>
          <DialogDescription className="sr-only">{message}</DialogDescription>
          <DialogClose
            className="text-primary-foreground/80 hover:text-primary-foreground inline-flex items-center transition-colors"
            aria-label={t("review.customer.residence.addressVerification.close")}
          >
            <X className="size-3.5" aria-hidden="true" />
          </DialogClose>
        </div>

        <div className="flex flex-col gap-4 bg-white px-5 py-3">
          <p className="text-sm">{message}</p>

          <div className="flex flex-col gap-4 sm:flex-row">
            <AddressDisplayPanel
              title={t("review.customer.residence.addressVerification.youEntered")}
              fields={entered}
            />
            {state.status === "WarningChanged" && suggested && (
              <AddressDisplayPanel
                title={t("review.customer.residence.addressVerification.uspsSuggested")}
                fields={suggested}
                invalid={suggestedInvalid}
              />
            )}
          </div>

          {confirmingIgnore && state.status !== "WarningChanged" && (
            <p role="alert" className="text-destructive text-sm">
              {t(IGNORE_CONFIRM_KEYS[state.status])}
            </p>
          )}
        </div>

        <DialogFooter className="bg-muted border-t px-5 py-2 sm:justify-center">
          {state.status === "WarningChanged" ? (
            <>
              <Button type="button" variant="outline" size="xs" onClick={onCancel}>
                {t("review.customer.residence.addressVerification.cancel")}
              </Button>
              <Button type="button" size="xs" onClick={handleSelectEntered}>
                {t("review.customer.residence.addressVerification.selectEntered")}
              </Button>
              <Button type="button" size="xs" onClick={handleSelectSuggested} disabled={suggestedInvalid}>
                {t("review.customer.residence.addressVerification.selectSuggested")}
              </Button>
            </>
          ) : confirmingIgnore ? (
            <>
              <Button type="button" variant="outline" size="xs" onClick={() => setConfirmingIgnore(false)}>
                {t("review.customer.residence.addressVerification.goBack")}
              </Button>
              <Button type="button" size="xs" onClick={onIgnore}>
                {t("review.customer.residence.addressVerification.confirmIgnore")}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" size="xs" onClick={onCancel}>
                {t("review.customer.residence.addressVerification.cancel")}
              </Button>
              <Button type="button" variant="outline" size="xs" onClick={() => setConfirmingIgnore(true)}>
                {t("review.customer.residence.addressVerification.ignore")}
              </Button>
              <Button type="button" size="xs" onClick={handleEditAddress}>
                {t("review.customer.residence.addressVerification.editAddress")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
